import { Directive, ElementRef, HostListener } from '@angular/core'

/**
 * Lightweight replacement for Bootstrap's JS dropdown plugin so we can drop
 * bootstrap.bundle.js. Toggles `.show` (and the `data-bs-popper` attribute that
 * Bootstrap's own CSS uses to position the menu at top:100%) on the sibling
 * `.dropdown-menu` inside the nearest `.dropdown` container. Closes on outside
 * click and Escape — i.e. always behaves like Bootstrap's auto-close="outside",
 * which is what the filter-form dropdowns rely on and is harmless for the info
 * panels. Bootstrap's dropdown CSS is retained; only its JS is removed.
 */
@Directive({
  selector: '[appDropdownToggle]',
  standalone: true
})
export class DropdownToggleDirective {
  private isOpen = false

  constructor(private el: ElementRef<HTMLElement>) {}

  private get menu(): HTMLElement | null {
    const container = this.el.nativeElement.closest('.dropdown')
    return container
      ? container.querySelector<HTMLElement>('.dropdown-menu')
      : null
  }

  @HostListener('click', ['$event'])
  onToggleClick(event: Event): void {
    event.stopPropagation()
    if (this.isOpen) this.close()
    else this.open()
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isOpen) return
    const container = this.el.nativeElement.closest('.dropdown')
    if (container && !container.contains(event.target as Node)) this.close()
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close()
  }

  private open(): void {
    const menu = this.menu
    if (!menu) return
    menu.classList.add('show')
    menu.setAttribute('data-bs-popper', 'static')
    this.el.nativeElement.setAttribute('aria-expanded', 'true')
    this.isOpen = true
  }

  private close(): void {
    const menu = this.menu
    if (menu) {
      menu.classList.remove('show')
      menu.removeAttribute('data-bs-popper')
    }
    this.el.nativeElement.setAttribute('aria-expanded', 'false')
    this.isOpen = false
  }
}
